import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import { useEventItems, useMarketItems } from 'modules/common/hooks';
import PrebuiltBetUtils from 'modules/prebuilt-bets/prebuilt-bet/utils.js';
import { uniq, uniqBy, isNil } from 'mojito/utils';

const { utils: EventUtils } = MojitoServices.SportsContent.Events;
const { selectOddsFormat } = MojitoServices.UserSettings.selectors;
const { selectAllowEarlyPayout } = MojitoServices.UserInfo.selectors;
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const toIds = (parts, key) => uniq(parts.map(part => part[key]).filter(Boolean));
const byId = items => Object.fromEntries(items.map(item => [item.id, item]));

/**
 * Resolves the metadata of each prebuilt bet part from its own event and market:
 * start time, neutral venue flag, current and base price and early payout.
 * Performs data requests for the part events and markets only when enabled and requests are allowed.
 *
 * @function usePartsMetadata
 *
 * @param {Array<object>} [parts] - Prebuilt bet parts.
 * @param {boolean} enabled - Set true to resolve metadata.
 * @param {object} l10n - Localization object used to format prices.
 * @param {boolean} [shouldRequestData = true] - If false, then part events and markets are not requested and metadata is resolved from the data already available in a store.
 *
 * @returns {object} Parts metadata keyed by part id. Empty object if disabled.
 * @memberof Mojito.Modules.PrebuiltBets.Hooks
 */
export const usePartsMetadata = (parts, enabled, l10n, shouldRequestData = true) => {
    const { eventIds, marketIds } = useMemo(() => {
        if (!enabled || !parts?.length) {
            return { eventIds: EMPTY_ARRAY, marketIds: EMPTY_ARRAY };
        }
        return { eventIds: toIds(parts, 'eventId'), marketIds: toIds(parts, 'marketId') };
    }, [enabled, parts]);

    const shouldRequest = enabled && shouldRequestData;
    const events = useEventItems(eventIds, shouldRequest);
    const markets = useMarketItems(marketIds, shouldRequest);
    const oddsFormat = useSelector(selectOddsFormat);
    const allowEarlyPayout = useSelector(selectAllowEarlyPayout);

    return useMemo(() => {
        if (!enabled || !parts?.length) {
            return EMPTY_OBJECT;
        }
        const eventsById = byId(events);
        const marketsById = byId(markets);
        const formatOdds = price => price && EventUtils.getOdds(price, oddsFormat, l10n);

        return Object.fromEntries(
            parts.map(part => {
                const event = eventsById[part.eventId];
                const market = marketsById[part.marketId];
                const selection = EventUtils.getSelectionFromMarket(market, part.id);
                const metadata = {
                    eventStartTime: event?.startTime,
                    neutral: Boolean(event?.neutral),
                    price: selection && formatOdds(EventUtils.getPrice(selection.prices)),
                    basePrice: formatOdds(selection?.basePrice),
                    // Prebuilt parts carry no early payout of their own, it belongs to the part market.
                    earlyPayout: allowEarlyPayout
                        ? PrebuiltBetUtils.getEarlyPayout(market)
                        : undefined,
                };
                return [part.id, metadata];
            })
        );
    }, [enabled, parts, events, markets, oddsFormat, allowEarlyPayout, l10n]);
};

/**
 * Limits the rendered prebuilt bet parts to the configured threshold until the user expands them.
 *
 * @function useLimitedParts
 *
 * @param {Array<object>} [parts] - Prebuilt bet parts.
 * @param {number} partsThreshold - Maximum number of parts shown while minimized. If -1 then all parts are always shown.
 *
 * @returns {{limitedParts: Array<object>, minimized: boolean, showAllButtonVisible: boolean, onToggleShowAllButton: Function}} Parts to render and the show all button state.
 * @memberof Mojito.Modules.PrebuiltBets.Hooks
 */
export const useLimitedParts = (parts, partsThreshold) => {
    const [minimized, setMinimized] = useState(true);
    const isLimited = partsThreshold !== -1;
    const showAllButtonVisible = isLimited && parts?.length > partsThreshold;

    const limitedParts = useMemo(
        () => (minimized && isLimited ? parts?.slice(0, partsThreshold) : parts),
        [minimized, isLimited, parts, partsThreshold]
    );

    const onToggleShowAllButton = e => {
        e.stopPropagation();
        setMinimized(!minimized);
    };

    return { limitedParts, minimized, showAllButtonVisible, onToggleShowAllButton };
};

/**
 * Resolves the market and selection a prebuilt bet is placed with.
 * When the selection is price boosted, the boosted market and selection take precedence and the formatted base price is returned.
 *
 * @function usePrebuiltBetInfo
 *
 * @param {object} [market] - Prebuilt bet market.
 * @param {object} [boostedMarket] - Boosted prebuilt bet market.
 * @param {object} l10n - Localization object used to format the base price.
 *
 * @returns {{market: object, selection: object, basePrice: string|undefined}} Prebuilt bet info. `basePrice` is only set for price boosted selections.
 * @memberof Mojito.Modules.PrebuiltBets.Hooks
 */
export const usePrebuiltBetInfo = (market, boostedMarket, l10n) => {
    const oddsFormat = useSelector(selectOddsFormat);
    const selection = market?.selections?.[0];
    const boostedSelection = boostedMarket?.selections?.[0];
    const basePrice = selection?.basePrice || boostedSelection?.basePrice;

    if (!basePrice) {
        return { market, selection };
    }

    return {
        market: boostedMarket || market,
        selection: boostedSelection || selection,
        basePrice: EventUtils.getOdds(basePrice, oddsFormat, l10n),
    };
};

const omitParticipantName = (marketName, eventName) => {
    const participants = eventName.split(/ v | @ /);
    const participantInMarket = participants.find(participant => marketName.includes(participant));
    return participantInMarket ? marketName.replace(participantInMarket, '').trim() : marketName;
};

/**
 * Derives a shared market subheader for prebuilt acca cards when every part uses the same market type
 * and the same market name after participant names are stripped from each part's market name.
 *
 * @function useSameMarketSubHeader
 *
 * @param {Array<object>} [parts] - Prebuilt bet builder parts from the acca market.
 *
 * @returns {{isSameMarketBet: boolean, subHeader: string|undefined}} Whether parts share one market type and the subheader text to show when they also share one market name.
 * @memberof Mojito.Modules.PrebuiltBets.Hooks
 */
export const useSameMarketSubHeader = parts => {
    const isSameMarketBet = Boolean(
        parts?.length &&
            parts.every(part => !isNil(part.marketType)) &&
            uniqBy(parts, part => part.marketType).length === 1
    );

    const subHeader = useMemo(() => {
        if (isSameMarketBet) {
            const marketNames = parts.map(part =>
                omitParticipantName(part.marketName, part.eventName)
            );
            return uniq(marketNames).length === 1 ? marketNames[0] : undefined;
        }
    }, [isSameMarketBet, parts]);

    return { isSameMarketBet, subHeader };
};

const { PREBUILT_MATCH_ACCA, PREBUILT_MATCH_ACCA_BOOST } =
    MojitoServices.Analytics.types.SELECTION_TYPE;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;

/**
 * Builds the analytics context for prebuilt match acca (bet builder) cards.
 *
 * @function useBetBuilderAnalyticsContext
 *
 * @param {object} [boostedMarket] - Boosted prebuilt bet market; when set, the boost selection analytics path is used.
 *
 * @returns {object} Analytics context for selection tracking on the card.
 * @memberof Mojito.Modules.PrebuiltBets.Hooks
 */
export const useBetBuilderAnalyticsContext = boostedMarket =>
    useMemo(() => {
        const path = boostedMarket ? PREBUILT_MATCH_ACCA_BOOST : PREBUILT_MATCH_ACCA;
        return new AnalyticsContextBuilder().withAnalyticsPath(path).build();
    }, [boostedMarket]);
